/**
 * Reading links on the device (decisions 58, 63; principle 9; part 38d log, principles DP1–DP8, tables LW, PL and RV):
 * making a link (its secrets here, its record and pinned snapshots stored before the relay hears of it, its copy sealed
 * in parts and sent), pulling its comments with the owner proof into note records and acknowledging them once stored,
 * replying in a reader's thread, revoking by record (pull, revoke, pull again), and the account's list and revoke-all by
 * session. Every effect is injected: the store and the history (the workspace's), the relay (session and public),
 * WebCrypto, the randomness and the clock, so it runs against the relay in tests.
 */
import { encodeItem, MAX_ITEM_TEXT, readItem, toHex } from '@cockatrice/core';
import type { ReadingItem } from '@cockatrice/core';
import { ITEM_ID_BYTES, IV_BYTES, noteIdOf, openItem, openWriterItem, partsFor, sealedSize, sealParts, sealWriterItem, shareKeysOf, TAG_BYTES, WRITER_ITEM_TAG_BYTES } from '@cockatrice/crypto';
import type { ShareKeys } from '@cockatrice/crypto';
import type { CreateResult, NoteRecord, ShareLinkEnd, ShareLinkRecord } from '@cockatrice/storage';
import { toBase64Url } from '../account/bytes.ts';
import type { RelayClient, RelayError, Reply } from '../account/client.ts';
import { noteOfItem } from './notes.ts';
import { bytesOfHex, newLinkSecrets, readerUrl, recordIdOf, secretsOf } from './secrets.ts';
import type { LinkSecrets, Random } from './secrets.ts';
import { createBody, hasEnded, MAX_ACK_IDS, MAX_ITEM_BYTES, ownerBody, partFrame, readAcknowledged, readLinkList, readPulled, readReplied, readRevoked, readRevokedAll, readUpload } from './wire.ts';
import type { LinkList, LinkState, Upload } from './wire.ts';

/** The account's routes, with its session (`Account`). */
export interface SessionRelay {
  get<T>(path: string, read: (value: unknown) => T | null): Promise<Reply<T>>;
  post<T>(path: string, body: unknown, read: (value: unknown) => T | null): Promise<Reply<T>>;
  postBytes<T>(path: string, bytes: Uint8Array<ArrayBuffer>, read: (value: unknown) => T | null): Promise<Reply<T>>;
}

/** A chapter as a link shares it: its ID, its title as the preview shows it, its text. */
export interface SharedChapter {
  readonly id: string;
  readonly title: string;
  readonly text: string;
}

/** What the links keep in the local store, through the workspace (its lock, its history). */
export interface ShareStore {
  /** Every reading-link record this device holds (the readable ones). */
  links(): Promise<readonly ShareLinkRecord[]>;
  /** The record of link `id`, or undefined (none, or refused). */
  link(id: string): Promise<ShareLinkRecord | undefined>;
  /** The readers' notes standing in book `bookId`, counted by the link's record they came through. */
  readerNotes(bookId: string): Promise<ReadonlyMap<string, number>>;
  /** Deep-fix r2, PL′1: whether chapter `id` is in its book here: an entry, not in the trash. */
  chapterHere(id: string): Promise<boolean>;
  /** Deep-fix r2, PF: the notes stored by a pull or a reply read again for the views, once. */
  notesStored(): Promise<void>;
  /** The note under `id`, or undefined (none, or refused: a refused one is held all the same, `create` says so). */
  note(id: string): Promise<NoteRecord | undefined>;
  /** Stores a new note under its ID only if no record holds it (C6, DP5), with no lock. */
  createNote(note: NoteRecord): Promise<CreateResult>;
  /** Stores a new link's record under its ID only if no record holds it (DP3), with no lock. */
  createLink(record: ShareLinkRecord): Promise<CreateResult>;
  /** Writes `record` again (its end, its upload), under the lock; false when it wasn't written. */
  put(record: ShareLinkRecord): Promise<boolean>;
  /** Deletes `record` (a link the relay refused to make), under the lock, with its pins; false when it wasn't. */
  remove(record: ShareLinkRecord): Promise<boolean>;
  /** DP3: pins each of `chapters` of `bookId` in a snapshot by link `id`; the snapshot of each, in order, or null (not written). */
  pin(bookId: string, chapters: readonly SharedChapter[], id: string): Promise<readonly string[] | null>;
  /** DP7: drops link `record`'s pins from its snapshots; false when that wasn't written. */
  unpin(record: ShareLinkRecord): Promise<boolean>;
}

/** What the links run on. */
export interface ShareDeps {
  readonly session: SessionRelay;
  /** The relay without a session (the owner's routes, LP1), or null with accounts off. */
  readonly relay: () => RelayClient | null;
  readonly store: ShareStore;
  readonly subtle: SubtleCrypto;
  readonly random: Random;
  readonly now: () => number;
  /** The reader page's origin, where the links point. */
  readonly readerOrigin: string;
}

/** What the writer asks for (the dialog's form). */
export interface LinkRequest {
  readonly bookId: string;
  readonly name: string;
  readonly chapters: readonly SharedChapter[];
  readonly comments: boolean;
  /** The end the writer set, or null. */
  readonly expiresAt: number | null;
  /** The copy's bytes (`encodeReadingCopy`). */
  readonly copy: Uint8Array;
}

/** A link made on the device whose parts haven't all gone up: what a retry sends again. */
export interface PendingLink {
  readonly record: ShareLinkRecord;
  readonly secrets: LinkSecrets;
  readonly parts: readonly Uint8Array<ArrayBuffer>[];
}

/** What making a link did. */
export type MakeOutcome =
  | { readonly kind: 'ready'; readonly record: ShareLinkRecord; readonly url: string }
  /** Not all its parts went up (the network): {@link ShareLinks.upload} again with `pending`. */
  | { readonly kind: 'unfinished'; readonly pending: PendingLink }
  /** The relay said no (decision 63's caps, a purchase needed): nothing kept. */
  | { readonly kind: 'refused'; readonly error: RelayError }
  /** The copy holds too much to share (part 38's 30 parts). */
  | { readonly kind: 'too-large' }
  /** The store didn't take the record or the pins (the lock went to another tab, or it failed). */
  | { readonly kind: 'failed' };

/** What a pull did. */
export type PullOutcome =
  | { readonly kind: 'pulled'; readonly state: LinkState; readonly stored: number; readonly unreadable: number }
  /** The relay holds the link no more (purged after its 90 days, or with the account): the record is ended `gone`. */
  | { readonly kind: 'gone' }
  | { readonly kind: 'failed' };

/** What a reply did. */
export type ReplyOutcome =
  | { readonly kind: 'sent'; readonly note: NoteRecord }
  /** The note isn't a reader's thread of a link this device holds and that may still be live: reply locally. */
  | { readonly kind: 'local' }
  | { readonly kind: 'refused'; readonly error: RelayError }
  /** Deep-fix r1, RT2: more than an item holds (its words, or its sealed bytes): nothing sent, nothing stored. */
  | { readonly kind: 'too-long' }
  | { readonly kind: 'failed' };

/** What a revoke did. */
export type RevokeOutcome = { readonly kind: 'revoked' } | { readonly kind: 'failed' };

/**
 * Deep-fix r1, RT2: whether `text` fits a reply the reader page will read: within the item's words (`MAX_ITEM_TEXT`)
 * and, sealed with its JSON, within the relay's item (`MAX_ITEM_BYTES`).
 */
export function replyFits(text: string): boolean {
  return text.length <= MAX_ITEM_TEXT && IV_BYTES + encodeItem({ kind: 'reply', to: '0'.repeat(32), text, name: '' }).length + TAG_BYTES + WRITER_ITEM_TAG_BYTES <= MAX_ITEM_BYTES;
}

/** A reply on its way (deep-fix r3, r5): its words, its item and its sealed bytes. */
interface Unanswered {
  readonly text: string;
  readonly item: Uint8Array<ArrayBuffer>;
  readonly body: Promise<Uint8Array<ArrayBuffer>>;
}

/** How many times a part is sent before the upload is left unfinished. */
export const PART_TRIES = 3;

const FAILED = Object.freeze({ kind: 'failed' as const });

/** Why a link ended, from the relay's state of an ended link. */
function endOf(state: Exclude<LinkState, 'uploading' | 'live'>): ShareLinkEnd {
  return state;
}

/** The reading links. */
export class ShareLinks {
  readonly #deps: ShareDeps;
  /**
   * Deep-fix r3, RS: each thread's reply whose answer was lost, by the thread's first note: its words, item and sealed
   * bytes, sent again as they were while the words are the same, so a reply the relay stored is never stored twice
   * (part 38's LA17: the same ID, thread and bytes is `done`). Kept for this page's life.
   */
  readonly #unanswered = new Map<string, Unanswered>();

  constructor(deps: ShareDeps) {
    this.#deps = deps;
  }

  /** The link a reader opens for `record`. */
  async urlOf(record: ShareLinkRecord): Promise<string> {
    return readerUrl(this.#deps.readerOrigin, await secretsOf(this.#deps.subtle, record));
  }

  /**
   * LW1, DP1–DP3: makes a link of `request`: its secrets, its pinned snapshots and its record first (DP3), then the
   * relay's create with the proof (never the secret, DP2), then the parts. A refusal deletes the record and its pins (the
   * relay holds nothing); a network failure leaves it to retry, or to the relay's sweep (`abandoned`).
   */
  async make(request: LinkRequest): Promise<MakeOutcome> {
    const { subtle, random, now, store, session } = this.#deps;
    const parts = partsFor(request.copy.length);
    if (parts === null) return { kind: 'too-large' };
    const secrets = await newLinkSecrets(subtle, random);
    const id = recordIdOf(secrets);
    const snapshots = await store.pin(request.bookId, request.chapters, id);
    if (snapshots === null) return FAILED;
    const record: ShareLinkRecord = {
      id,
      bookId: request.bookId,
      name: request.name,
      key: toHex(secrets.key),
      secret: toHex(secrets.secret),
      chapters: request.chapters.map((chapter) => chapter.id),
      snapshots,
      comments: request.comments,
      expiresAt: request.expiresAt,
      createdAt: now(),
      size: sealedSize(request.copy.length, parts),
      uploaded: false,
      ended: null,
    };
    if ((await store.createLink(record)).kind !== 'created') {
      await store.unpin(record);
      return FAILED;
    }
    const sealed = await sealParts(subtle, await shareKeysOf(subtle, secrets.key), secrets.id, request.copy);
    const made = await session.post('/v1/links', createBody(secrets.link, secrets.proof, record.size, record.comments, record.expiresAt), readUpload);
    if (made.kind === 'refused') {
      await store.remove(record);
      return { kind: 'refused', error: made.error };
    }
    const pending: PendingLink = { record, secrets, parts: sealed };
    return made.kind === 'ok' ? this.#send(pending, made.value) : { kind: 'unfinished', pending };
  }

  /** LW1: sends `pending`'s parts again from where the relay says it stands (a retry after {@link make} was left unfinished). */
  async upload(pending: PendingLink): Promise<MakeOutcome> {
    const { secrets, record } = pending;
    const made = await this.#deps.session.post('/v1/links', createBody(secrets.link, secrets.proof, record.size, record.comments, record.expiresAt), readUpload);
    if (made.kind === 'refused') return { kind: 'refused', error: made.error };
    return made.kind === 'ok' ? this.#send(pending, made.value) : { kind: 'unfinished', pending };
  }

  /** The parts from `upload.nextPart` on, each tried {@link PART_TRIES} times; the record marked uploaded once the link is live. */
  async #send(pending: PendingLink, upload: Upload): Promise<MakeOutcome> {
    const { record, secrets, parts } = pending;
    if (hasEnded(upload.state)) return { kind: 'refused', error: 'link-ended' };
    let state: LinkState = upload.state;
    for (const [index, part] of parts.entries()) {
      if (index < upload.nextPart) continue;
      let sent: Reply<Upload> = FAILED;
      for (let tries = 0; tries < PART_TRIES && sent.kind === 'failed'; tries += 1) sent = await this.#deps.session.postBytes('/v1/links/part', partFrame(secrets.link, index, part), readUpload);
      // Table SB: the relay's places for slow bodies all taken (or the account's rate) is a wait, not a refusal.
      if (sent.kind === 'refused' && sent.error !== 'rate-limited') return { kind: 'refused', error: sent.error };
      if (sent.kind !== 'ok') return { kind: 'unfinished', pending };
      state = sent.value.state;
    }
    if (state !== 'live') return { kind: 'unfinished', pending };
    const uploaded = { ...record, uploaded: true };
    await this.#deps.store.put(uploaded);
    return { kind: 'ready', record: uploaded, url: readerUrl(this.#deps.readerOrigin, secrets) };
  }

  /**
   * Table PL, DP5, DP6, S5, C6: pulls link `record`'s items with its owner proof, page after page, each stored as its note
   * (once: a note there already is left as it is), then the page's stored ones acknowledged; an item that doesn't open or
   * read is skipped and never acknowledged. An ended link's record is ended (DP7); one the relay holds no more, `gone`.
   */
  async pull(record: ShareLinkRecord): Promise<PullOutcome> {
    const pulled = await this.#pull(record);
    // Deep-fix r2, PF: the views read the notes again once a pull is done, never once a note.
    if (pulled.stored > 0) await this.#deps.store.notesStored();
    return pulled.outcome;
  }

  /** {@link pull}'s work: its outcome, and how many notes it stored (on a failure too). */
  async #pull(record: ShareLinkRecord): Promise<{ readonly outcome: PullOutcome; readonly stored: number }> {
    const { subtle, store } = this.#deps;
    const relay = this.#deps.relay();
    if (relay === null) return { outcome: FAILED, stored: 0 };
    const secrets = await secretsOf(subtle, record);
    const keys = await shareKeysOf(subtle, secrets.key);
    let after = 0;
    let stored = 0;
    let unreadable = 0;
    for (;;) {
      const page = await relay.post('/v1/links/pull', ownerBody(secrets.link, secrets.proof, { after }), readPulled);
      if (page.kind === 'refused' && page.error === 'link-gone') {
        await this.#end(record, 'gone');
        return { outcome: { kind: 'gone' }, stored };
      }
      if (page.kind !== 'ok') return { outcome: FAILED, stored };
      const acknowledge: string[] = [];
      for (const item of page.value.items) {
        const note = await this.#noteOf(record, secrets, keys, item);
        if (note === null) {
          unreadable += 1;
          continue;
        }
        const created = await store.createNote(note);
        if (created.kind === 'failed') return { outcome: FAILED, stored };
        if (created.kind === 'created') stored += 1;
        acknowledge.push(toBase64Url(item.id));
      }
      for (let at = 0; at < acknowledge.length; at += MAX_ACK_IDS) {
        // PL: an acknowledgement lost leaves the items for the next pull, which finds their notes stored.
        await relay.post('/v1/links/ack', ownerBody(secrets.link, secrets.proof, { ids: acknowledge.slice(at, at + MAX_ACK_IDS) }), readAcknowledged);
      }
      after = page.value.items.at(-1)?.seq ?? after;
      if (!page.value.more || page.value.items.length === 0) {
        const { state } = page.value;
        if (hasEnded(state)) await this.#end(record, endOf(state));
        // Deep-fix r4, PL′3: a link the relay holds live whose record says its upload didn't finish (the last part's
        // answer lost, the dialog closed, a write lost) has finished: the record says so.
        else if (state === 'live') await this.#uploaded(record);
        return { outcome: { kind: 'pulled', state, stored, unreadable }, stored };
      }
    }
  }

  /** DP4, DP6, table PL: the note `item` becomes, or null when it doesn't open, doesn't read, or is no note to keep. */
  async #noteOf(record: ShareLinkRecord, secrets: LinkSecrets, keys: ShareKeys, item: Parameters<typeof noteOfItem>[1]): Promise<NoteRecord | null> {
    const { subtle, store, now } = this.#deps;
    // Fix-next 84, DP9: an item labelled the writer's is one only with the writer's tag, which the owner secret alone makes.
    const plain = item.author === 'writer' ? await openWriterItem(subtle, keys, secrets.secret, secrets.id, item.id, item.body) : await openItem(subtle, keys, secrets.id, item.id, item.author, item.body);
    const opened: ReadingItem | null = plain === null ? null : readItem(plain);
    if (opened === null) return null;
    const id = toHex(await noteIdOf(subtle, secrets.id, item.id));
    const answered = opened.kind === 'reply' ? await store.note(toHex(await noteIdOf(subtle, secrets.id, bytesOfHex(opened.to)))) : undefined;
    // Deep-fix r9, table PR: a reply's thread is its first note's, whatever became of the reply it names.
    const first = answered?.parentId === null || answered?.parentId === undefined ? answered : await store.note(answered.parentId);
    const chapterHere = opened.kind === 'comment' && (await store.chapterHere(opened.chapter));
    return noteOfItem(record, item, opened, { id, answered, first, chapterHere, now: now() });
  }

  /** RS′: a new reply of `text` to item `to`: a fresh item ID, its sealing begun under a fresh IV. */
  #newReply(keys: ShareKeys, secrets: LinkSecrets, to: string, text: string): Unanswered {
    const { subtle, random } = this.#deps;
    const item = random(ITEM_ID_BYTES);
    // DP9: sealed as the writer's, with the writer's tag.
    return { text, item, body: sealWriterItem(subtle, keys, secrets.secret, secrets.id, item, encodeItem({ kind: 'reply', to, text, name: '' }), random(IV_BYTES)) };
  }

  /** PL′3: `record` marked uploaded, as stored now, unless it is already. */
  async #uploaded(record: ShareLinkRecord): Promise<void> {
    const current = (await this.#deps.store.link(record.id)) ?? record;
    if (!current.uploaded) await this.#deps.store.put({ ...current, uploaded: true });
  }

  /** DP7: `record` ended `why` at the relay's word, its pins dropped; once. */
  async #end(record: ShareLinkRecord, why: ShareLinkEnd): Promise<void> {
    const { store, now } = this.#deps;
    const current = (await store.link(record.id)) ?? record;
    // Deep-fix r3, EN: the first end is kept and its pins dropped then, once; the relay's purge (`gone`) is the last word
    // on any end, after which no pull asks again.
    if (current.ended === null) {
      await store.put({ ...current, ended: { at: now(), why } });
      await store.unpin(current);
    } else if (why === 'gone' && current.ended.why !== 'gone') {
      await store.put({ ...current, ended: { at: current.ended.at, why } });
    }
  }

  /**
   * LW1, SL11: the writer's reply `text` to the reader's thread `note` is in: sealed as the writer's item under the link's
   * key and sent with the owner proof, then stored as the reply it is (the next pull finds it there, DP5). `local` when
   * `note` isn't a thread of a link this device holds that hasn't ended.
   */
  async reply(note: NoteRecord, text: string): Promise<ReplyOutcome> {
    const { subtle, store, now } = this.#deps;
    const relay = this.#deps.relay();
    const root = note.parentId === null ? note : await store.note(note.parentId);
    const link = root?.link;
    const record = link === undefined ? undefined : await store.link(link.link);
    // Deep-fix r1, RT1: a thread of this book's own link only (a copy of the book keeps its notes' links, not the link).
    if (root === undefined || link === undefined || record?.ended !== null || record.bookId !== root.bookId || relay === null) return { kind: 'local' };
    if (!replyFits(text)) return { kind: 'too-long' };
    const secrets = await secretsOf(subtle, record);
    const keys = await shareKeysOf(subtle, secrets.key);
    const kept = this.#unanswered.get(root.id);
    // Deep-fix r5, RS′: kept before it goes, in the same turn as the look, so the same words sent again meanwhile are the
    // same item (the relay's `done`).
    const entry = kept?.text === text ? kept : this.#newReply(keys, secrets, link.item, text);
    this.#unanswered.set(root.id, entry);
    const { item } = entry;
    const body = await entry.body;
    const sent = await relay.post('/v1/links/reply', ownerBody(secrets.link, secrets.proof, { thread: toBase64Url(bytesOfHex(link.thread)), id: toBase64Url(item), body: toBase64Url(body) }), readReplied);
    if (sent.kind === 'refused') {
      this.#unanswered.delete(root.id);
      if (sent.error === 'link-ended' || sent.error === 'link-gone' || sent.error === 'not-found') await this.pull(record);
      return { kind: 'refused', error: sent.error };
    }
    if (sent.kind === 'failed') return FAILED;
    this.#unanswered.delete(root.id);
    const at = now();
    const reply: NoteRecord = {
      id: toHex(await noteIdOf(subtle, secrets.id, item)),
      bookId: record.bookId,
      chapterId: root.chapterId,
      anchor: null,
      title: '',
      group: '',
      origin: 'writer',
      author: '',
      sourceKey: null,
      visibility: 'only-me',
      parentId: root.id,
      resolved: false,
      text,
      writtenAt: at,
      storedAt: at,
      editedAt: at,
      deletedAt: null,
      link: { link: record.id, thread: link.thread, item: toHex(item) },
    };
    await store.createNote(reply);
    await store.notesStored();
    return { kind: 'sent', note: reply };
  }

  /** Table RV, DP7: revokes `record` by its owner proof: pull, revoke, pull again; the record ended at the relay's word. */
  async revoke(record: ShareLinkRecord): Promise<RevokeOutcome> {
    const relay = this.#deps.relay();
    if (relay === null) return FAILED;
    const first = await this.pull(record);
    if (first.kind === 'gone') return { kind: 'revoked' };
    const secrets = await secretsOf(this.#deps.subtle, record);
    const revoked = await relay.post('/v1/links/revoke', ownerBody(secrets.link, secrets.proof), readRevoked);
    if (revoked.kind === 'refused' && revoked.error === 'link-gone') {
      await this.#end(record, 'gone');
      return { kind: 'revoked' };
    }
    if (revoked.kind !== 'ok') return FAILED;
    // The comments sent before the revoke reached the relay; the second pull takes them and ends the record.
    const last = await this.pull(record);
    if (last.kind === 'failed' && hasEnded(revoked.value)) await this.#end(record, endOf(revoked.value));
    return { kind: 'revoked' };
  }

  /** LW3, table RV: ends every link of the account by session, then pulls each record this device holds; how many ended. */
  async revokeAll(): Promise<number | null> {
    const ended = await this.#deps.session.post('/v1/links/revoke-all', {}, readRevokedAll);
    if (ended.kind !== 'ok') return null;
    for (const record of await this.#deps.store.links()) await this.pull(record);
    return ended.value;
  }

  /** LW4: the account's links as the relay lists them by session, or null. */
  async list(): Promise<LinkList | null> {
    const listed = await this.#deps.session.get('/v1/links', readLinkList);
    return listed.kind === 'ok' ? listed.value : null;
  }
}
